import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { useCallback } from "react";

import { Icon } from "@/components";

import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { useToDoSocket } from "../providers/SocketContext";
import { SubTaskInfo, TaskInfo } from "../../../TasksAndSubTasks/schema/tasks-and-subtasks";

interface ToDoSubtaskCheckBoxProps {
	data?: TaskInfo;
	subTask: SubTaskInfo;
	className?: string;
	subTaskNameShowing?: boolean;
	/** Blocked transitions still render the box, greyed, with the reason on hover. */
	disabled?: boolean;
	disabledReason?: string;
}

export default function ToDoSubtaskCheckBox({
	data,
	subTask,
	className,
	subTaskNameShowing = true,
	disabled,
	disabledReason,
}: ToDoSubtaskCheckBoxProps) {
	const isDone = subTask?.status === ToDoStatus.DONE;
	const { changeSubTaskStatus } = useToDoSocket();

	const changeStatusHandler = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.stopPropagation();

			if (disabled) {
				return;
			}

			if (subTask?.taskId && subTask.id) {
				changeSubTaskStatus(
					subTask.taskId,
					+subTask.id,
					isDone ? ToDoStatus.IN_PROGRESS : ToDoStatus.DONE
				);
			}
		},
		[changeSubTaskStatus, subTask?.taskId, isDone, subTask.id, disabled]
	);

	return (
		<button
			aria-checked={isDone}
			className="h-fit w-fit flex items-start gap-2"
			disabled={disabled}
			role="checkbox"
			title={disabled ? disabledReason : undefined}
			type="button"
			onClick={changeStatusHandler}
		>
			<div
				className={`mt-1 min-w-5 min-h-5 max-w-5 max-h-5 flex items-center justify-center border rounded-[4px] transition-all ${
					isDone ? "bg-primary-blue border-primary-blue" : "bg-surface border-primary-gray-lightest"
				} ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
			>
				{isDone && <Icon className="text-white w-[14px] h-[14px]" icon={faCheck} />}
			</div>
			<div className="w-full text-left">
				<span className="text-[14px] font-semibold text-left leading-5 text-primary-blue-dark mb-2">
					{subTaskNameShowing ? subTask?.title : null}
				</span>
			</div>
		</button>
	);
}
